import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { getSelectedLocationIds } from '@app/data/helpers/locationHelpers';
import { EChartsOption } from 'echarts';
import { getKaupapaChartConfig, KaupapaChartData } from './rongo-kaupapa-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-kaupapa-chart',
  templateUrl: './rongo-kaupapa-chart.component.html',
  styleUrls: ['./rongo-kaupapa-chart.component.scss'],
})
export class RongoKaupapaChartComponent implements OnInit {
  chartTitle: string = 'Rongo Kaupapa';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-kaupapa';
  startDate: any = '';
  endDate: any = '';
  locationIds: string[] = [];
  data: KaupapaChartData[] | [] = [];
  availableOptions: string[] = [];
  selectedOption: string = 'All';
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<KaupapaChartData[]>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.locationIds = getSelectedLocationIds(this.filterService.getFilters().selectedLocations);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getKaupapaData(this.startDate, this.endDate, this.locationIds, 'organisation').subscribe(
      (data: RongoReportingData<KaupapaChartData[]>) => {
        const { config, data: kaupapaData, breakdown } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.data = kaupapaData;
        this.breakdown = breakdown?.groups || null;
        this.noData = kaupapaData.length === 0;
        this.availableOptions = ['All', ...Array.from(new Set(kaupapaData.map((item) => item.visitPurpose)))];
        this.selectedOption = 'All';
        this.setChartData();
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };

  setChartData = () => {
    const filterData = (data: KaupapaChartData[]) =>
      this.selectedOption === 'All' ? data : data.filter((item) => item.visitPurpose === this.selectedOption);

    this.chartOptions = withOrganisationBreakdown(
      getKaupapaChartConfig(filterData(this.data as KaupapaChartData[])),
      this.breakdown,
      {
        buildGroupOption: (d) => getKaupapaChartConfig(filterData(d)),
        flatten: (d: KaupapaChartData[]) => {
          const totals: Record<string, number> = {};
          d.forEach((item) => {
            totals[item.projectName] = (totals[item.projectName] || 0) + item.totalObservations;
          });
          return Object.entries(totals).map(([name, value]) => ({ name, value }));
        },
      }
    );
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const newLocationIds = getSelectedLocationIds(filters.filters.selectedLocations);
    const locationsChanged = newLocationIds.join(',') !== this.locationIds.join(',');
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || locationsChanged || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.locationIds = newLocationIds;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  handlePurposeChange(event: MatSelectChange): void {
    this.setChartData();
  }
}
